import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { cn } from "@/shared/utils/general/cn"
import { is24HourFormat } from "@/shared/utils/date/dateUtils"
import { hapticLight } from "@/shared/utils/platform/haptics"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const ITEM_HEIGHT = 40 // px per option
const VISIBLE_COUNT = 3 // visible items in viewport (1 above, selected, 1 below)
const VIEWPORT_HEIGHT = ITEM_HEIGHT * VISIBLE_COUNT // 120px

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface WheelTimePickerProps {
  hour: number // 0-23
  minute: number // 0-59
  onTimeChange: (hour24: number, minute: number) => void
  /** Override auto-detected format */
  format?: "12h" | "24h"
  /** When true, columns expand to fill available width (better touch targets) */
  fullWidth?: boolean
  className?: string
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pad(n: number): string {
  return n.toString().padStart(2, "0")
}

function generateHours(is24h: boolean): Array<{ value: number; label: string }> {
  if (is24h) {
    return Array.from({ length: 24 }, (_, i) => ({ value: i, label: pad(i) }))
  }
  return Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: pad(i + 1),
  }))
}

function generateMinutes(): Array<{ value: number; label: string }> {
  return Array.from({ length: 60 }, (_, i) => ({ value: i, label: pad(i) }))
}

function to24Hour(hour12: number, period: "AM" | "PM"): number {
  if (period === "AM") return hour12 === 12 ? 0 : hour12
  return hour12 === 12 ? 12 : hour12 + 12
}

function to12Hour(hour24: number): { hour: number; period: "AM" | "PM" } {
  if (hour24 === 0) return { hour: 12, period: "AM" }
  if (hour24 < 12) return { hour: hour24, period: "AM" }
  if (hour24 === 12) return { hour: 12, period: "PM" }
  return { hour: hour24 - 12, period: "PM" }
}

// ---------------------------------------------------------------------------
// Single Wheel Column
// ---------------------------------------------------------------------------

interface WheelColumnProps {
  items: Array<{ value: number | string; label: string }>
  selectedValue: number | string
  onSelect: (value: number | string) => void
  /** Signal this column wants to enter edit mode */
  onRequestEdit?: () => void
  /** Text alignment within column */
  align?: "left" | "center" | "right"
  /** Inline edit mode — freezes scroll, makes center item editable */
  editMode?: boolean
  /** Props for inline editing */
  editProps?: {
    value: string
    minValue: number
    maxValue: number
    onCommit: (value: number) => void
    onAdvance: () => void
    onReverse: () => void
    onExit: () => void
    focused: boolean
    onFocus: () => void
    inputRef: React.RefObject<HTMLInputElement | null>
  }
}

const WheelColumn = memo(function WheelColumn({
  items,
  selectedValue,
  onSelect,
  onRequestEdit,
  align = "center",
  editMode = false,
  editProps,
}: WheelColumnProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const isScrollingRef = useRef(false)
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const isUserScrollRef = useRef(true)
  const [visualCenter, setVisualCenter] = useState<number | string>(selectedValue)

  // Triple the items for infinite scroll illusion
  const tripled = useMemo(() => [...items, ...items, ...items], [items])
  const middleOffset = items.length * ITEM_HEIGHT

  // Scroll to selected value (in the middle set)
  const scrollToValue = useCallback(
    (value: number | string, smooth = false) => {
      if (!scrollRef.current) return
      const index = items.findIndex((it) => it.value === value)
      if (index === -1) return
      const targetScroll = middleOffset + index * ITEM_HEIGHT
      isUserScrollRef.current = false
      scrollRef.current.scrollTo({
        top: targetScroll,
        behavior: smooth ? "smooth" : "instant",
      })
      requestAnimationFrame(() => {
        isUserScrollRef.current = true
      })
    },
    [items, middleOffset],
  )

  // Initial scroll
  useEffect(() => {
    scrollToValue(selectedValue, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Restore scroll when exiting edit mode
  useEffect(() => {
    if (!editMode) {
      // Small delay to let the scroll DOM re-render
      requestAnimationFrame(() => {
        scrollToValue(selectedValue, false)
        setVisualCenter(selectedValue)
      })
    }
  }, [editMode, selectedValue, scrollToValue])

  // Update scroll when value changes from parent
  useEffect(() => {
    if (!isScrollingRef.current) {
      scrollToValue(selectedValue, false)
      setVisualCenter(selectedValue)
    }
  }, [selectedValue, scrollToValue])

  // Handle scroll — update visual center in real-time, snap + select on stop
  const handleScroll = useCallback(() => {
    if (editMode) return // Freeze scroll in edit mode
    if (!scrollRef.current || !isUserScrollRef.current) return

    const scrollTop = scrollRef.current.scrollTop
    const centerIndex = Math.round(scrollTop / ITEM_HEIGHT)
    const actualIndex = ((centerIndex % items.length) + items.length) % items.length
    const centerItem = items[actualIndex]
    if (centerItem) {
      setVisualCenter(centerItem.value)
    }

    isScrollingRef.current = true
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)

    scrollTimeoutRef.current = setTimeout(() => {
      if (!scrollRef.current) return
      isScrollingRef.current = false

      const st = scrollRef.current.scrollTop
      const snappedIndex = Math.round(st / ITEM_HEIGHT)
      const snapActualIndex = ((snappedIndex % items.length) + items.length) % items.length
      const selectedItem = items[snapActualIndex]
      if (selectedItem && selectedItem.value !== selectedValue) {
        onSelect(selectedItem.value)
        hapticLight()
      }

      // Wrap around
      if (st < ITEM_HEIGHT * 2) {
        isUserScrollRef.current = false
        scrollRef.current.scrollTop = st + middleOffset
        requestAnimationFrame(() => { isUserScrollRef.current = true })
      } else if (st > middleOffset * 2 - ITEM_HEIGHT * 2) {
        isUserScrollRef.current = false
        scrollRef.current.scrollTop = st - middleOffset
        requestAnimationFrame(() => { isUserScrollRef.current = true })
      }
    }, 80)
  }, [editMode, items, middleOffset, onSelect, selectedValue])

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current)
    }
  }, [])

  // ── Inline edit state (local value + keyboard) ──
  const [localEditValue, setLocalEditValue] = useState(editProps?.value ?? "")
  useEffect(() => {
    if (editProps?.value) setLocalEditValue(editProps.value)
  }, [editProps?.value])

  useEffect(() => {
    if (editMode && editProps?.focused && editProps?.inputRef.current) {
      editProps.inputRef.current.focus()
      editProps.inputRef.current.select()
    }
  }, [editMode, editProps?.focused, editProps?.inputRef])

  const commitEdit = useCallback((v: string) => {
    if (!editProps) return
    const num = parseInt(v, 10)
    if (!isNaN(num) && num >= 0 && num <= editProps.maxValue) {
      editProps.onCommit(num)
    }
  }, [editProps])

  // Get neighbor items for static display in edit mode
  const selectedIndex = useMemo(
    () => items.findIndex((it) => it.value === selectedValue),
    [items, selectedValue],
  )
  const prevItem = useMemo(
    () => items[(selectedIndex - 1 + items.length) % items.length],
    [items, selectedIndex],
  )
  const nextItem = useMemo(
    () => items[(selectedIndex + 1) % items.length],
    [items, selectedIndex],
  )

  // ── Render: edit mode shows static 3-item view with inline input ──
  if (editMode && editProps) {
    return (
      <div className="relative" style={{ height: VIEWPORT_HEIGHT }}>
        {/* Highlight bar — same as normal mode */}
        <div
          className={cn(
            "pointer-events-none absolute left-3 right-3 rounded-lg",
            align === "right" ? "bg-gradient-to-r from-transparent to-muted/60"
              : align === "left" ? "bg-gradient-to-r from-muted/60 to-transparent"
              : "bg-muted/60",
          )}
          style={{
            top: ITEM_HEIGHT * Math.floor(VISIBLE_COUNT / 2),
            height: ITEM_HEIGHT,
          }}
        />

        {/* Static 3-item stack */}
        <div className="relative z-10 flex h-full flex-col items-stretch justify-center">
          {/* Previous item */}
          <div
            className={cn(
              "flex items-center justify-center font-mono",
              align === "right" ? "pl-24" : align === "left" ? "pr-24" : "",
            )}
            style={{ height: ITEM_HEIGHT }}
          >
            <span className="text-base text-muted-foreground/30" style={{ transform: "scale(0.85)" }}>
              {prevItem?.label}
            </span>
          </div>

          {/* Center item — editable input */}
          <div
            className={cn(
              "flex items-center justify-center font-mono",
              align === "right" ? "pl-24" : align === "left" ? "pr-24" : "",
            )}
            style={{ height: ITEM_HEIGHT }}
          >
            <input
              ref={editProps.inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={localEditValue}
              onFocus={() => {
                editProps.onFocus()
                setTimeout(() => editProps.inputRef.current?.select(), 0)
              }}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, "").slice(0, 2)
                setLocalEditValue(v)
                if (v.length === 2) {
                  commitEdit(v)
                  setTimeout(() => editProps.onAdvance(), 50)
                }
              }}
              onKeyDown={(e) => {
                if (e.key === "Tab") {
                  e.preventDefault()
                  commitEdit(localEditValue)
                  if (e.shiftKey) editProps.onReverse()
                  else editProps.onAdvance()
                } else if (e.key === "Enter") {
                  commitEdit(localEditValue)
                  editProps.onExit()
                } else if (e.key === "Escape") {
                  editProps.onExit()
                } else if (e.key === "ArrowUp") {
                  e.preventDefault()
                  const num = parseInt(localEditValue, 10)
                  const next = isNaN(num) ? editProps.minValue : num >= editProps.maxValue ? editProps.minValue : num + 1
                  editProps.onCommit(next)
                  hapticLight()
                } else if (e.key === "ArrowDown") {
                  e.preventDefault()
                  const num = parseInt(localEditValue, 10)
                  const next = isNaN(num) ? editProps.maxValue : num <= editProps.minValue ? editProps.maxValue : num - 1
                  editProps.onCommit(next)
                  hapticLight()
                }
              }}
              onWheel={(e) => {
                e.preventDefault()
                e.stopPropagation()
                const num = parseInt(localEditValue, 10)
                if (e.deltaY < 0) {
                  const next = isNaN(num) ? editProps.minValue : num >= editProps.maxValue ? editProps.minValue : num + 1
                  editProps.onCommit(next)
                  hapticLight()
                } else {
                  const next = isNaN(num) ? editProps.maxValue : num <= editProps.minValue ? editProps.maxValue : num - 1
                  editProps.onCommit(next)
                  hapticLight()
                }
              }}
              onBlur={() => {
                commitEdit(localEditValue)
              }}
              className="w-14 bg-transparent text-center font-mono text-2xl font-bold text-foreground outline-none caret-primary selection:bg-primary/20"
            />
          </div>

          {/* Next item */}
          <div
            className={cn(
              "flex items-center justify-center font-mono",
              align === "right" ? "pl-24" : align === "left" ? "pr-24" : "",
            )}
            style={{ height: ITEM_HEIGHT }}
          >
            <span className="text-base text-muted-foreground/30" style={{ transform: "scale(0.85)" }}>
              {nextItem?.label}
            </span>
          </div>
        </div>

        {/* Top/bottom fades — same as normal mode */}
        <div
          className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-card via-card/80 to-transparent"
          style={{ height: ITEM_HEIGHT * Math.floor(VISIBLE_COUNT / 2) }}
        />
        <div
          className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-card via-card/80 to-transparent"
          style={{ height: ITEM_HEIGHT * Math.floor(VISIBLE_COUNT / 2) }}
        />
      </div>
    )
  }

  // ── Normal scroll mode ──
  return (
    <div className="relative" style={{ height: VIEWPORT_HEIGHT }}>
      {/* Highlight bar — inset with gradient on outer edge */}
      <div
        className={cn(
          "pointer-events-none absolute left-3 right-3 rounded-lg",
          align === "right" ? "bg-gradient-to-r from-transparent to-muted/60"
            : align === "left" ? "bg-gradient-to-r from-muted/60 to-transparent"
            : "bg-muted/60",
        )}
        style={{
          top: ITEM_HEIGHT * Math.floor(VISIBLE_COUNT / 2),
          height: ITEM_HEIGHT,
        }}
      />

      {/* Scrollable area */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        onWheel={e => e.stopPropagation()}
        className="relative z-10 h-full snap-y snap-mandatory overflow-y-auto overscroll-contain hide-scrollbar"
        style={{
          scrollSnapType: "y mandatory",
          paddingTop: ITEM_HEIGHT * Math.floor(VISIBLE_COUNT / 2),
          paddingBottom: ITEM_HEIGHT * Math.floor(VISIBLE_COUNT / 2),
        }}
      >
        {tripled.map((item, i) => {
          const isCentered = item.value === visualCenter
          return (
            <div
              key={`${item.value}-${i}`}
              className={cn(
                "flex items-center justify-center snap-center font-mono",
                "transition-all duration-100",
                align === "right" ? "pl-24" : align === "left" ? "pr-24" : "",
              )}
              style={{ height: ITEM_HEIGHT }}
              onClick={() => {
                if (isCentered && onRequestEdit) {
                  onRequestEdit()
                }
              }}
            >
              <span
                className={cn(
                  "transition-all duration-100",
                  isCentered
                    ? "text-2xl font-bold text-foreground"
                    : "text-base text-muted-foreground/30",
                )}
                style={{ transform: isCentered ? "scale(1)" : "scale(0.85)" }}
              >
                {item.label}
              </span>
            </div>
          )
        })}
      </div>

      {/* Top/bottom fades */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-card via-card/80 to-transparent"
        style={{ height: ITEM_HEIGHT * Math.floor(VISIBLE_COUNT / 2) }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-card via-card/80 to-transparent"
        style={{ height: ITEM_HEIGHT * Math.floor(VISIBLE_COUNT / 2) }}
      />
    </div>
  )
})

// ---------------------------------------------------------------------------
// Period Toggle (AM/PM in edit mode)
// ---------------------------------------------------------------------------

interface PeriodToggleProps {
  period: "AM" | "PM"
  onToggle: () => void
  onAdvance: () => void
  onReverse: () => void
  onExit: () => void
  focused: boolean
  onFocus: () => void
  inputRef: React.RefObject<HTMLButtonElement | null>
}

const PeriodToggle = memo(function PeriodToggle({
  period,
  onToggle,
  onAdvance,
  onReverse,
  onExit,
  focused,
  onFocus,
  inputRef,
}: PeriodToggleProps) {
  useEffect(() => {
    if (focused && inputRef.current) {
      inputRef.current.focus()
    }
  }, [focused, inputRef])

  return (
    <button
      ref={inputRef}
      type="button"
      onClick={() => {
        onFocus()
        onToggle()
        hapticLight()
      }}
      onFocus={onFocus}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onToggle()
        } else if (e.key === "Tab") {
          e.preventDefault()
          if (e.shiftKey) onReverse()
          else onAdvance()
        } else if (e.key === "Escape") {
          onExit()
        } else if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault()
          onToggle()
        }
      }}
      onWheel={(e) => {
        e.preventDefault()
        onToggle()
        hapticLight()
      }}
      className={cn(
        "w-12 rounded-md py-1 text-center font-mono text-xl font-bold outline-none transition-colors",
        focused
          ? "bg-muted text-foreground"
          : "text-muted-foreground/60 hover:text-foreground",
      )}
    >
      {period}
    </button>
  )
})

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

/**
 * Scroll-wheel time picker with tap-to-type edit mode.
 *
 * - Scroll/swipe to select (iOS drum style)
 * - Double-tap center value → enters edit mode for all fields
 * - Tab advances between fields, auto-advance after 2 digits
 * - Escape or click outside exits edit mode
 */
export const WheelTimePicker = memo(function WheelTimePicker({
  hour,
  minute,
  onTimeChange,
  format,
  fullWidth,
  className,
}: WheelTimePickerProps) {
  const is24h = useMemo(
    () => (format === "12h" ? false : format === "24h" ? true : is24HourFormat()),
    [format],
  )

  const hours = useMemo(() => generateHours(is24h), [is24h])
  const minutes = useMemo(() => generateMinutes(), [])

  const { hour: hour12, period } = useMemo(() => to12Hour(hour), [hour])

  // ── Edit mode state ──
  const [editMode, setEditMode] = useState(false)
  const [focusedField, setFocusedField] = useState<"hour" | "minute" | "period">("hour")
  const hourInputRef = useRef<HTMLInputElement>(null)
  const minuteInputRef = useRef<HTMLInputElement>(null)
  const periodInputRef = useRef<HTMLButtonElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const enterEditMode = useCallback((field: "hour" | "minute" | "period" = "hour") => {
    setEditMode(true)
    setFocusedField(field)
  }, [])

  const exitEditMode = useCallback(() => {
    setEditMode(false)
  }, [])

  // Exit edit mode when clicking outside
  useEffect(() => {
    if (!editMode) return
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        exitEditMode()
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [editMode, exitEditMode])

  const advanceFrom = useCallback(
    (field: "hour" | "minute" | "period", reverse = false) => {
      const fields: Array<"hour" | "minute" | "period"> = is24h
        ? ["hour", "minute"]
        : ["hour", "minute", "period"]
      const idx = fields.indexOf(field)
      const next = reverse
        ? (idx - 1 + fields.length) % fields.length
        : (idx + 1) % fields.length
      setFocusedField(fields[next] ?? "hour")
    },
    [is24h],
  )

  // ── Scroll handlers ──
  const handleHourSelect = useCallback(
    (value: number | string) => {
      const h = typeof value === "number" ? value : parseInt(value as string, 10)
      if (is24h) {
        onTimeChange(h, minute)
      } else {
        onTimeChange(to24Hour(h, period), minute)
      }
    },
    [is24h, minute, onTimeChange, period],
  )

  const handleMinuteSelect = useCallback(
    (value: number | string) => {
      const m = typeof value === "number" ? value : parseInt(value as string, 10)
      onTimeChange(hour, m)
    },
    [hour, onTimeChange],
  )

  const handlePeriodSelect = useCallback(
    (value: number | string) => {
      const newPeriod = value as "AM" | "PM"
      onTimeChange(to24Hour(hour12, newPeriod), minute)
    },
    [hour12, minute, onTimeChange],
  )

  // ── Edit commit handlers ──
  const handleHourCommit = useCallback(
    (value: number) => {
      if (is24h) {
        const clamped = Math.min(23, Math.max(0, value))
        onTimeChange(clamped, minute)
      } else {
        const clamped = Math.min(12, Math.max(1, value))
        onTimeChange(to24Hour(clamped, period), minute)
      }
    },
    [is24h, minute, onTimeChange, period],
  )

  const handleMinuteCommit = useCallback(
    (value: number) => {
      const clamped = Math.min(59, Math.max(0, value))
      onTimeChange(hour, clamped)
    },
    [hour, onTimeChange],
  )

  const handlePeriodToggle = useCallback(() => {
    const newPeriod = period === "AM" ? "PM" : "AM"
    onTimeChange(to24Hour(hour12, newPeriod), minute)
  }, [hour12, minute, onTimeChange, period])

  const periodItems = useMemo(
    () => [
      { value: "AM" as const, label: "AM" },
      { value: "PM" as const, label: "PM" },
    ],
    [],
  )

  return (
    <div ref={containerRef} className={cn("relative flex items-center justify-center gap-1", fullWidth && "w-full", className)}>
      {/* Hours */}
      <div className={fullWidth ? "flex-1" : "w-12"}>
        <WheelColumn
          items={hours}
          selectedValue={is24h ? hour : hour12}
          onSelect={handleHourSelect}
          onRequestEdit={() => enterEditMode("hour")}
          align="right"
          editMode={editMode}
          editProps={editMode ? {
            value: pad(is24h ? hour : hour12),
            minValue: is24h ? 0 : 1,
            maxValue: is24h ? 23 : 12,
            onCommit: handleHourCommit,
            onAdvance: () => advanceFrom("hour"),
            onReverse: () => advanceFrom("hour", true),
            onExit: exitEditMode,
            focused: focusedField === "hour",
            onFocus: () => setFocusedField("hour"),
            inputRef: hourInputRef,
          } : undefined}
        />
      </div>

      {/* Colon separator */}
      <span className="pb-0.5 font-mono text-lg font-semibold text-muted-foreground">
        :
      </span>

      {/* Minutes */}
      <div className={fullWidth ? "flex-1" : "w-12"}>
        <WheelColumn
          items={minutes}
          selectedValue={minute}
          onSelect={handleMinuteSelect}
          onRequestEdit={() => enterEditMode("minute")}
          align="left"
          editMode={editMode}
          editProps={editMode ? {
            value: pad(minute),
            minValue: 0,
            maxValue: 59,
            onCommit: handleMinuteCommit,
            onAdvance: () => advanceFrom("minute"),
            onReverse: () => advanceFrom("minute", true),
            onExit: exitEditMode,
            focused: focusedField === "minute",
            onFocus: () => setFocusedField("minute"),
            inputRef: minuteInputRef,
          } : undefined}
        />
      </div>

      {/* Confirm button (edit mode) — floating, absolutely positioned */}
      {editMode && (
        <button
          type="button"
          onClick={exitEditMode}
          className="absolute -right-1 top-1/2 z-20 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-primary/90 text-primary-foreground transition-colors hover:bg-primary"
          aria-label="Confirm time"
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 8.5 6.5 12 13 4" />
          </svg>
        </button>
      )}

      {/* AM/PM (12h only) */}
      {!is24h && (
        <div className={cn("ml-1", fullWidth ? "flex-1" : "w-12")}>
          {editMode ? (
            <div className="flex items-center justify-center" style={{ height: VIEWPORT_HEIGHT }}>
              <PeriodToggle
                period={period}
                onToggle={handlePeriodToggle}
                onAdvance={() => advanceFrom("period")}
                onReverse={() => advanceFrom("period", true)}
                onExit={exitEditMode}
                focused={focusedField === "period"}
                onFocus={() => setFocusedField("period")}
                inputRef={periodInputRef}
              />
            </div>
          ) : (
            <WheelColumn
              items={periodItems}
              selectedValue={period}
              onSelect={handlePeriodSelect}
              onRequestEdit={() => enterEditMode("period")}
            />
          )}
        </div>
      )}
    </div>
  )
})
